import { Tooltip } from '@material-ui/core';
import { isEmpty } from 'lodash';
import React, {
  memo,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import trans from 'translation';
import { KButton, KColors, KContainer, KGrid } from 'uikit';
import { v4 as uuidv4 } from 'uuid';

interface IProps extends PropsWithChildren {
  defaultTitle?: string | string[];
  initIndex?: number;
  rightNode?: JSX.Element;
}

const Container = (props: IProps) => {
  const { children, rightNode, defaultTitle, initIndex } = props;
  const id = useMemo(() => uuidv4(), []);
  const pages: ReactNode[] = useMemo(() => {
    const list: ReactNode[] = [];

    React.Children.map(children, child => {
      list.push(child);
    });

    return list;
  }, [children]);

  const hasMultiplePages = useMemo(() => pages.length > 1, [pages.length]);

  const titles = useMemo(
    () =>
      isEmpty(pages) ? [defaultTitle] : pages?.map((p: any) => p?.props?.title),
    [defaultTitle, pages]
  );

  const containerRef = useRef<HTMLDivElement>(null);

  const [dragging, setDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const [pageIndex, setPageIndex] = useState(initIndex ?? 0);
  const [autoPaginationEnabled, setAutoPaginationEnabled] = useState(true);

  const onMouseDown = useCallback((e: any) => {
    setDragging(true);
    setStartX(e.clientX);
  }, []);

  const onMouseMove = useCallback(
    (e: any) => {
      if (!dragging || !hasMultiplePages) return;

      const moveX = e.clientX - startX;
      setTranslateX(moveX);
    },
    [dragging, hasMultiplePages, startX]
  );

  const onMouseUp = useCallback(() => {
    if (!hasMultiplePages) return;

    setDragging(false);
    setStartX(0);

    if (translateX > 150) {
      setPageIndex(prev => (prev === 0 ? pages.length - 1 : prev - 1));
    } else if (translateX < -150) {
      setPageIndex(prev => (prev === pages.length - 1 ? 0 : prev + 1));
    }

    setTranslateX(0);
  }, [hasMultiplePages, pages.length, translateX]);

  useEffect(() => {
    const ref = containerRef.current;
    if (!ref) return;

    const handleMouseEnter = () => setAutoPaginationEnabled(false);
    const handleMouseLeave = () => setAutoPaginationEnabled(true);

    ref.addEventListener('mouseenter', handleMouseEnter);
    ref.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      ref.removeEventListener('mouseenter', handleMouseEnter);
      ref.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  useEffect(() => {
    if (!autoPaginationEnabled || !hasMultiplePages) return;

    const timeout = setTimeout(() => {
      setPageIndex((prev: any) => (prev >= pages.length - 1 ? 0 : prev + 1));
    }, 3000);

    return () => clearTimeout(timeout);
  }, [autoPaginationEnabled, hasMultiplePages, pageIndex, pages.length]);

  const renderDots = useMemo(() => {
    return pages?.map((p: any, idx) => (
      <Tooltip
        key={`dot-${idx}-carousel-${id}`}
        title={p?.props?.title ?? trans('unknown')}
        placement="top"
      >
        <span>
          <KButton.Icon
            icon="FiberManualRecord"
            tintColor={
              pageIndex === idx ? KColors.primary.normal : KColors.normal.normal
            }
            onPress={() => setPageIndex(idx)}
            size="xs"
            avoidParentPress
          />
        </span>
      </Tooltip>
    ));
  }, [id, pageIndex, pages]);

  return (
    <KContainer.Card
      ref={containerRef}
      header={{
        title: titles[pageIndex].toUpperCase(),
        rightNode,
        typo: 'TextMdBold'
      }}
      overflow="hidden"
    >
      <KGrid.Container>
        <KGrid.Item xs={12}>
          <KContainer.View
            width={'100%'}
            style={{
              transform: `translateX(${translateX}px)`,
              transition: dragging ? 'none' : 'transform 0.2s ease'
            }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
          >
            {pages[pageIndex]}
          </KContainer.View>
        </KGrid.Item>
        <KContainer.RenderWhen>
          <KContainer.RenderWhen.If isTrue={pages.length > 1}>
            <KGrid.Item xs={12}>
              <KContainer.View
                dp="flex"
                justifyContent="center"
                alignItems="center"
                row
              >
                {renderDots}
              </KContainer.View>
            </KGrid.Item>
          </KContainer.RenderWhen.If>
        </KContainer.RenderWhen>
      </KGrid.Container>
    </KContainer.Card>
  );
};

export default memo(Container);
